import React, { useRef, useState } from "react";
import AxiosInstance from "../authorization/AxiosInstance";

/**
 * ImageUploader
 * Props:
 *   value    — current image URL (shown as preview)
 *   onChange — called with the new S3 URL after a successful upload
 *   folder   — S3 sub-folder (optional, default "uploads")
 */
export default function ImageUploader({ value, onChange, folder = "uploads" }) {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setError(null);
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const res = await AxiosInstance.post(`/upload?folder=${folder}`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      onChange(res.data.url);
    } catch (err) {
      setError(err?.response?.data?.message || "Upload failed");
    } finally {
      setUploading(false);
      // reset so the same file can be re-selected
      e.target.value = "";
    }
  };

  return (
    <div>
      {/* Preview */}
      {value && (
        <div className="mb-2">
          <img
            src={value}
            alt="preview"
            style={{ maxHeight: 140, maxWidth: "100%", borderRadius: 8, objectFit: "cover" }}
          />
        </div>
      )}

      {/* URL input + Upload button side by side */}
      <div className="flex gap-2 items-center">
        <input
          type="text"
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          placeholder="Paste image URL or upload a file"
          className="flex-1 border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
        />
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          disabled={uploading}
          className="flex-shrink-0 bg-green-600 hover:bg-green-700 text-white text-xs font-semibold px-4 py-2 rounded-lg disabled:opacity-50 transition-colors"
        >
          {uploading ? "Uploading…" : "Upload"}
        </button>
      </div>

      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={handleFile}
      />

      {error && <p className="text-red-500 text-xs mt-1">{error}</p>}
    </div>
  );
}
